import axios, { isAxiosError } from 'axios'
import { router } from '@/plugins/1.router'
import { eventBus } from '@/plugins/eventBus'
import { setUser } from "@/composables/useUser.js"
import axiosRetry, { exponentialDelay } from 'axios-retry'

// Tạo một phương thức để cấu hình Axios
function createApiClient() {
  const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL + '/' + import.meta.env.VITE_API_BASE_PREFIX,
    headers: {
      Accept: 'application/json',
    },
    timeout: 20000,
  })

  // Sử dụng axios-retry để tự động retry các yêu cầu gặp lỗi
  axiosRetry(apiClient, { retries: 2, retryDelay: exponentialDelay })

  // Interceptor để thêm Authorization token vào tất cả các yêu cầu
  apiClient.interceptors.request.use(config => {
    const accessToken = useCookie('accessToken').value
    if (accessToken) {
      config.headers.Authorization = 'Bearer ' + accessToken
    }

    return config
  }, error => Promise.reject(error))

  return apiClient
}

const handleUnauthorized = () => {
  const accessToken = useCookie('accessToken').value
  if (accessToken) {
    useCookie('accessToken').value = null

    setUser({
      id: null,
      username: "",
      name: "",
      email: "",
      avatar: "",
      permissions: [],
    })

    router.push('/login')

    setTimeout(() => {
      eventBus.emit('push-error-login', "Phiên đăng nhập hết hạn!")
    }, 400)
  }
}

const errorActions = {
  401: handleUnauthorized,
  403: function () {
    router.push('/not-authorized')
  },
  500: function () {
    eventBus.emit('push-error', "Lỗi máy chủ, vui lòng thử lại sau.")
  },
}

export const handleCatch = error => {
  if (isAxiosError(error)) {
    const code = error.response?.status
    if (errorActions[code]) {
      errorActions[code]()
    } else {
      console.error(`Unhandled error code: ${code}`)
    }
  } else {
    console.error('Unexpected error:', error.message)
  }

  throw error
}

class ApiClient {
  constructor(client) {
    this.client = client
  }

  async request(method, path, body = null, config = {}) {
    try {
      const axiosConfig = {
        method,
        url: path,
        ...((method === 'GET') && { params: config.query }),
        ...((method !== 'GET') && { data: body, params: config.query }),
      }

      const res = await this.client.request(axiosConfig)

      if (res.status === 200 || res.status === 201) {
        return res.data
      } else {
        throw new Error('Yêu cầu không thành công')
      }
    } catch (error) {
      handleCatch(error)
    }
  }

  async get(path, config = {}) {
    return await this.request('GET', path, null, config)
  }

  async post(path, body, config = {}) {
    return await this.request('POST', path, body, config)
  }

  async put(path, body, config = {}) {
    return await this.request('PUT', path, body, config)
  }

  async delete(path, config = {}, body = null) {
    return await this.request('DELETE', path, body, config)
  }
}

const ApiService = new ApiClient(createApiClient())

export { ApiService }
